import type { PortalRole, SessionUser } from '@ucc/shared';
import {
  enterSupportGrant,
  exitSupportGrant,
  fetchAuthMe,
  login as apiLogin,
  logout as apiLogout,
} from './api';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';
export type ThemeMode = 'dark' | 'light';

const THEME_KEY = 'ucc.theme';
const SIDEBAR_KEY = 'ucc.sidebarCollapsed';
const WELCOME_AFTER_PASSWORD_KEY = 'ucc_welcome_after_password';

function readTheme(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'dark';
  const v = localStorage.getItem(THEME_KEY);
  return v === 'light' ? 'light' : 'dark';
}

function readSidebarCollapsed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(SIDEBAR_KEY) === '1';
}

function applyTheme(theme: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

/**
 * Browser session backed by httpOnly cookie on the API.
 * Theme/sidebar are local preferences.
 */
class SessionStore {
  user = $state<SessionUser | null>(null);
  status = $state<SessionStatus>('loading');
  error = $state<string | null>(null);
  theme = $state<ThemeMode>(readTheme());
  sidebarCollapsed = $state(readSidebarCollapsed());
  /** One-time banner after forced password change + re-login. */
  welcomeBanner = $state<string | null>(null);

  constructor() {
    applyTheme(this.theme);
  }

  get isAuthenticated(): boolean {
    return this.status === 'authenticated' && this.user !== null;
  }

  get role(): PortalRole {
    return this.user?.role ?? 'user';
  }

  get displayName(): string {
    return this.user?.displayName ?? 'Guest';
  }

  get email(): string {
    return this.user?.email ?? '';
  }

  get tenantName(): string {
    return this.user?.tenantName ?? '';
  }

  /** §17 Milestone 3 — true when this staff session is acting under a support grant. */
  get isSupportActing(): boolean {
    return Boolean(this.user?.isSupportActing);
  }

  async enterSupport(tenantId: string): Promise<void> {
    const { user } = await enterSupportGrant(tenantId);
    this.user = user;
  }

  async exitSupport(): Promise<void> {
    const { user } = await exitSupportGrant();
    this.user = user;
  }

  async bootstrap(): Promise<void> {
    this.status = 'loading';
    this.error = null;
    try {
      const { user } = await fetchAuthMe();
      this.user = user;
      this.status = user ? 'authenticated' : 'anonymous';
      if (user) this.consumeWelcomeAfterPasswordChange();
    } catch (e) {
      this.user = null;
      this.status = 'anonymous';
      this.error = e instanceof Error ? e.message : 'Failed to restore session';
    }
  }

  async login(email: string, password: string): Promise<void> {
    this.error = null;
    const { user } = await apiLogin({ email, password });
    this.user = user;
    this.status = 'authenticated';
    this.consumeWelcomeAfterPasswordChange();
  }

  async logout(): Promise<void> {
    try {
      await apiLogout();
    } catch {
      // still clear local state
    }
    this.user = null;
    this.status = 'anonymous';
    this.welcomeBanner = null;
  }

  /** Call before forced re-login after password change. */
  markPendingWelcomeAfterPasswordChange(): void {
    try {
      sessionStorage.setItem(WELCOME_AFTER_PASSWORD_KEY, '1');
    } catch {
      /* private mode */
    }
  }

  /**
   * After login / boot: if password was just set, show banner once.
   * Returns true when the welcome was consumed (App may redirect to dashboard).
   */
  consumeWelcomeAfterPasswordChange(): boolean {
    try {
      if (sessionStorage.getItem(WELCOME_AFTER_PASSWORD_KEY) !== '1') return false;
      sessionStorage.removeItem(WELCOME_AFTER_PASSWORD_KEY);
    } catch {
      return false;
    }
    const name = this.user?.displayName?.trim() || 'there';
    this.welcomeBanner = `Welcome, ${name}! Your password was updated successfully. You are all set to use Unarvu Cloud Connect.`;
    return true;
  }

  dismissWelcomeBanner(): void {
    this.welcomeBanner = null;
  }

  setTheme(theme: ThemeMode): void {
    this.theme = theme;
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    try {
      localStorage.setItem(SIDEBAR_KEY, this.sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
}

export const session = new SessionStore();
